import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { LoadingSpinner, PageLoader } from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { dbRpc } from "../lib/api";
import { ArrowLeft, ChevronDown, ChevronUp, Coins, ExternalLink, RefreshCw } from "lucide-react";

const ADMIN_EMAIL = "a.burkov@gmail.com";

interface PaidUserDetail {
  user_email: string;
  user_display_name: string | null;
  credits_spent: number;
  credits_remaining: number;
}

interface ProjectCreditUsage {
  project_id: string;
  project_name: string;
  project_slug: string | null;
  project_public_id: string | null;
  owner_email: string | null;
  owner_display_name: string | null;
  total_credits: number;
  unique_paid_users: number;
}

export function AdminCreditUsagePage() {
  const { user, session, loading: authLoading } = useAuth();
  const [data, setData] = useState<ProjectCreditUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<Record<string, PaidUserDetail[]>>({});
  const [loadingUsers, setLoadingUsers] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Admin Credit Usage - Llambada";
    return () => {
      document.title = "Llambada";
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);

    try {
      const { data: rows, error: rpcError } = await dbRpc<
        ProjectCreditUsage[]
      >("get_paid_user_credit_usage_by_project", {}, { token: session.access_token });

      if (rpcError) throw rpcError;
      setData(rows || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch credit usage",
      );
    } finally {
      setLoading(false);
    }
  }, [session]);

  const toggleUsers = useCallback(async (projectId: string) => {
    if (expandedProject === projectId) {
      setExpandedProject(null);
      return;
    }
    setExpandedProject(projectId);
    if (userDetails[projectId]) return;

    setLoadingUsers(projectId);
    try {
      const { data: users, error: rpcError } = await dbRpc<PaidUserDetail[]>(
        "get_paid_users_for_project",
        { p_project_id: projectId },
        { token: session!.access_token },
      );
      if (rpcError) throw rpcError;
      setUserDetails((prev) => ({ ...prev, [projectId]: users || [] }));
    } catch {
      setUserDetails((prev) => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingUsers(null);
    }
  }, [expandedProject, userDetails, session]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchData();
    }
  }, [user, fetchData]);

  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  const totalCredits = data.reduce((sum, row) => sum + row.total_credits, 0);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
            <Coins className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Paid User Credit Usage by Mini-App
            </h1>
            <p className="text-theme-text-secondary text-sm">
              Mini-apps ranked by credits spent by paying users (subscriptions
              &amp; PAYG).
            </p>
          </div>
          <button
            onClick={fetchData}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>

        {!loading && data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="card p-4">
              <div className="text-theme-text-muted text-sm font-medium mb-1">
                Total Credits (Paid Users)
              </div>
              <div className="text-2xl font-bold text-theme-text">
                {totalCredits.toLocaleString()}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-theme-text-muted text-sm font-medium mb-1">
                Mini-Apps Used
              </div>
              <div className="text-2xl font-bold text-theme-text">
                {data.length}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-theme-text-muted text-sm font-medium mb-1">
                Top Mini-App
              </div>
              <div className="text-lg font-bold text-theme-text truncate">
                {data[0]?.project_name || "-"}
              </div>
              <div className="text-sm text-theme-text-muted">
                {data[0]?.total_credits.toLocaleString() || 0} credits
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="card p-10 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : data.length === 0 ? (
          <div className="card p-10 text-center text-theme-text-muted">
            No credit usage from paying users found.
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-theme-surface border-b border-theme-border-muted">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted w-12">
                      #
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Mini-App
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Owner
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Credits Spent
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Paid Users
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      % of Total
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                      Links
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => {
                    const publicPath = row.project_public_id
                      ? `/p/${row.project_public_id}${row.project_slug ? `/${row.project_slug}` : ""}`
                      : null;
                    const pct =
                      totalCredits > 0
                        ? ((row.total_credits / totalCredits) * 100).toFixed(1)
                        : "0";

                    const isExpanded = expandedProject === row.project_id;
                    const users = userDetails[row.project_id];
                    const isLoadingThis = loadingUsers === row.project_id;

                    return (
                      <React.Fragment key={row.project_id}>
                        <tr
                          className="border-b border-theme-border-muted/60 last:border-b-0"
                        >
                          <td className="px-4 py-3 text-sm text-theme-text-muted">
                            {idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-theme-text">
                              {row.project_name}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-theme-text">
                              {row.owner_display_name || row.owner_email || "Unknown"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-mono font-semibold text-theme-text">
                              {row.total_credits.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => toggleUsers(row.project_id)}
                              className="inline-flex items-center gap-1 text-sm text-accent-400 hover:text-accent-300"
                            >
                              {row.unique_paid_users}
                              {isExpanded ? (
                                <ChevronUp className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-theme-text-muted">
                            {pct}%
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {publicPath ? (
                                <Link
                                  to={publicPath}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-accent-400 hover:text-accent-300"
                                >
                                  Open
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Link>
                              ) : (
                                <span className="text-sm text-theme-text-muted">
                                  Private
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-theme-surface/50">
                            <td colSpan={7} className="px-4 py-3">
                              {isLoadingThis ? (
                                <div className="flex justify-center py-2">
                                  <LoadingSpinner size="sm" />
                                </div>
                              ) : users && users.length > 0 ? (
                                <div className="ml-8">
                                  <table className="w-full">
                                    <thead>
                                      <tr>
                                        <th className="text-left px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">Email</th>
                                        <th className="text-left px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">Name</th>
                                        <th className="text-right px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">Spent</th>
                                        <th className="text-right px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">Remaining</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {users.map((u) => (
                                        <tr key={u.user_email} className="border-t border-theme-border-muted/40">
                                          <td className="px-3 py-1.5 text-sm text-theme-text">{u.user_email}</td>
                                          <td className="px-3 py-1.5 text-sm text-theme-text-muted">{u.user_display_name || "-"}</td>
                                          <td className="px-3 py-1.5 text-sm text-right font-mono text-theme-text">{u.credits_spent.toLocaleString()}</td>
                                          <td className="px-3 py-1.5 text-sm text-right font-mono text-theme-text">{(u.credits_remaining ?? 0).toLocaleString()}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-sm text-theme-text-muted text-center py-2">
                                  No paid user details found.
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
